import * as React from 'react';
import * as styles from './styles.scss';
import { observer } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import type { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import type { IIconProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { deleteAssets, getSelectedProjectId } from 'owa-timestream-store';
import type { SearchResultItem } from 'owa-timestream-search-orchestration';
import useAddSearchResultToCanvas from '../utils/useAddSearchResultToCanvas';
import { openPopoutTitle } from 'owa-locstrings/lib/strings/openpopouttitle.locstring.json';
import { removeTooltip } from 'owa-locstrings/lib/strings/removetooltip.locstring.json';
import { addToCanvasLabel, optionsAriaText } from './SearchResultOptions.locstring.json';

const iconProps: IIconProps = {
    iconName: ControlIcons.More,
    styles: {
        root: styles.assetEllipsisIcon,
    },
};

const dropdownIconProps: IIconProps = {
    className: styles.searchResultOptionEllipsisMenu,
};

const menuItemIconStyles: IIconStyles = {
    root: styles.menuItemIcon,
};

export default observer(function SearchResultOptions({
    assetRef,
    searchResultItem,
    assetType,
    onOpen,
    isFocusable,
    onOptionsOpened,
    onOptionsClosed,
}: {
    assetRef: React.RefObject<HTMLDivElement>;
    searchResultItem: SearchResultItem;
    assetType: string;
    onOpen: () => void;
    isFocusable: boolean;
    onOptionsOpened?: () => void;
    onOptionsClosed?: () => void;
}) {
    const projectId = getSelectedProjectId();
    const deleteAsset = React.useCallback(() => {
        deleteAssets(projectId, [searchResultItem.existingAssetId]);
    }, [projectId, searchResultItem.existingAssetId]);

    const addSearchResultToCanvas = useAddSearchResultToCanvas(assetType, searchResultItem);

    const addSearchResult = React.useCallback(() => {
        addSearchResultToCanvas(assetRef.current);
    }, []);

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'addToCanvas',
                text: loc(addToCanvasLabel),
                iconProps: {
                    iconName: isCurrentCultureRightToLeft()
                        ? ControlIcons.Forward
                        : ControlIcons.Back,
                    styles: menuItemIconStyles,
                },
                className: styles.menuItem,
                onClick: addSearchResult,
            },
            {
                key: 'openFullItem',
                text: loc(openPopoutTitle),
                iconProps: {
                    iconName: ControlIcons.MiniExpandMirrored,
                    styles: menuItemIconStyles,
                },
                className: styles.menuItem,
                onClick: onOpen,
            },
            ...(searchResultItem.existingAssetId
                ? [
                      {
                          key: 'remove',
                          text: loc(removeTooltip),
                          iconProps: {
                              iconName: ControlIcons.RemoveContent,
                              styles: menuItemIconStyles,
                          },
                          className: styles.menuItem,
                          onClick: deleteAsset,
                      },
                  ]
                : []),
        ],
        onMenuOpened: onOptionsOpened,
        onMenuDismissed: onOptionsClosed,
    };

    const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.which === KeyCodes.space || e.which === KeyCodes.enter) {
            e.stopPropagation();
        }
    }, []);

    return (
        <IconButton
            onKeyDown={onKeyDown}
            ariaLabel={loc(optionsAriaText)}
            data-is-focusable={isFocusable}
            className={styles.searchResultOptionsEllipsisButton}
            menuIconProps={dropdownIconProps}
            iconProps={iconProps}
            menuProps={menuProps}
        />
    );
});
